/**
 * Drop-in replacement for `@monaco-editor/react` that guarantees Monaco comes
 * from our bundled ESM build.
 *
 * Rendering `@monaco-editor/react` before `./bootstrap` has run makes its
 * loader fetch Monaco's AMD `loader.js` from a CDN. That script installs a
 * global `define`, which every UMD-wrapped dependency that loads afterwards
 * (body-scroll-lock, lodash, moment, ...) then mistakes for a real AMD loader
 * and crashes with "Can only have one anonymous define call per script file".
 * Import editors and `useMonaco` from here instead of `@monaco-editor/react`.
 */

import type {DiffEditorProps, EditorProps, Monaco} from '@monaco-editor/react';
import React, {useEffect, useState} from 'react';

import {importWithRetry} from '../../../../util/dynamicImportRetry';
import {isComponentModule} from '../../../../util/lazyImport';

export type {DiffEditorProps, EditorProps, Monaco};
export type {
  DiffOnMount,
  OnChange,
  OnMount,
  OnValidate,
} from '@monaco-editor/react';

type MonacoReactModule = typeof import('@monaco-editor/react');

// Each import is retried separately: retry re-imports the URL that failed and
// returns that module, so it cannot wrap a loader that imports twice.
async function loadMonacoReact(): Promise<MonacoReactModule> {
  await importWithRetry(() => import('./bootstrap'));
  return await importWithRetry(() => import('@monaco-editor/react'), {
    isExpectedModule: isComponentModule,
  });
}

const LazyEditor = React.lazy(async () => {
  const mod = await loadMonacoReact();
  return {default: mod.Editor};
});

const LazyDiffEditor = React.lazy(async () => {
  const mod = await loadMonacoReact();
  return {default: mod.DiffEditor};
});

export const Editor = (props: EditorProps) => (
  <React.Suspense fallback={props.loading ?? null}>
    <LazyEditor {...props} />
  </React.Suspense>
);

export const DiffEditor = (props: DiffEditorProps) => (
  <React.Suspense fallback={props.loading ?? null}>
    <LazyDiffEditor {...props} />
  </React.Suspense>
);

/**
 * Resolves to the bundled Monaco namespace once it has loaded, like
 * `useMonaco` from `@monaco-editor/react` but without triggering its loader.
 */
export function useMonaco(): Monaco | null {
  const [monaco, setMonaco] = useState<Monaco | null>(null);
  useEffect(() => {
    let cancelled = false;
    importWithRetry(() => import('./bootstrap')).then(({monacoEditor}) => {
      if (!cancelled) {
        setMonaco(monacoEditor);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return monaco;
}

export default Editor;
